"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, ArrowRight, PhoneCall, ShieldCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqs = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Ascot?",
  answer: "Yes. We regularly supply, erect, and dismantle scaffolding for domestic, commercial, and specialist projects across Ascot and surrounding Berkshire areas."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Ascot?",
  answer: "We provide access scaffolding, temporary roofs, edge protection, chimney scaffolds, birdcages, solar access setups, and specialist bespoke scaffolding configurations."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We design and install roof access scaffolds, chimney platforms, handrails, and temporary roofing systems tailored to roofing repairs, re-roofs, and installations."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We partner with main contractors, residential developers, roofing specialists, solar installers, and local facilities management teams with full compliance documentation."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our team conducts site surveys to assess narrow alleyways, conservatories, tight boundary lines, and restricted access points to build safe, bespoke structures."
}, {
  id: "item-6",
  question: "How do I request a quote in Ascot?",
  answer: "You can submit your project details and drawings through our online quote request form, or contact our team directly to arrange a site survey."
}];
export default function AscotFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="ascotfaq" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          {/* Header Column */}
          <motion.div initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }} className="lg:col-span-5 space-y-6">
            <div className="space-y-3">
              <Badge variant="secondary" className="gap-1.5 py-1 px-3">
                <HelpCircle className="size-3.5 text-primary" />
                <span>Ascot service queries</span>
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
                Frequently asked questions about Ascot scaffolding
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed">
                Clear answers to common questions about our scaffolding hire, site safety standards, and project access capabilities in Ascot.
              </p>
            </div>

            {/* Quick trust reassurance box */}
            <div className="p-6 rounded-xl bg-card border border-border shadow-xs space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-card-foreground">
                    Accredited scaffolding contractor
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    NASC and CHAS registered
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Every structure is erected by qualified scaffolding professionals following standard TG20 safety guidance and rigorous inspection routines.
              </p>
              <div className="pt-2">
                <Button asChild className="w-full sm:w-auto">
                  <Link to="/ContactRequestQuotePage">
                    Request a quote
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Accordion Column */}
          <div className="lg:col-span-7">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} className="bg-card rounded-2xl border border-border p-6 sm:p-8 shadow-xs">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                {faqs.map((faq, i) => <div data-index={i} key={faq.id}>
                    <motion.div variants={itemVariants}>
                      <AccordionItem value={faq.id} className="border-b border-border/80 last:border-b-0 py-1">
                        <AccordionTrigger className="text-base sm:text-lg font-medium text-left text-foreground hover:no-underline hover:text-primary transition-colors py-4">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    </motion.div>
                  </div>)}
              </Accordion>
            </motion.div>

            {/* Bottom prompt */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-xl bg-accent text-accent-foreground border border-border">
              <div className="flex items-center gap-3 text-sm">
                <PhoneCall className="size-4 text-primary shrink-0" />
                <span>Need immediate guidance on complex site access?</span>
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/ContactRequestQuotePage">Speak to our team</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}